(() => {
  if (window.__deportemaniaCartRecommendationsInitialized) return;
  window.__deportemaniaCartRecommendationsInitialized = true;

  const SLOT_ID = 'cart-drawer-recommendations';
  const SECTION_ID = 'cart-drawer-recommendations';
  const MAX_VISIBLE_RECOMMENDATIONS = 3;
  const REQUESTED_RECOMMENDATIONS = 10;
  const TOP_LEVEL_COLLECTION_DISCIPLINES = new Map([
    ['hockey', 'hockey'],
    ['fitness', 'fitness'],
    ['boxeo-y-artes-marciales', 'boxeo'],
    ['futbol', 'futbol'],
    ['voleibol', 'voleibol'],
    ['basquetbol', 'basquetbol'],
    ['ping-pong', 'ping-pong'],
    ['padel-tenis-badminton', 'racket'],
    ['natacion', 'natacion']
  ]);
  const RACKET_SUBDISCIPLINES = new Set(['padel', 'tenis', 'badminton', 'beach-paddle']);
  const GENERIC_COLLECTION_HANDLES = new Set([
    ...TOP_LEVEL_COLLECTION_DISCIPLINES.keys(),
    'ver-todos',
    'todos',
    'all'
  ]);
  let observer;
  let scheduledRefresh;
  let requestController;
  let refreshId = 0;

  const routeRoot = () => {
    const root = window.Shopify?.routes?.root || '/';
    return root.endsWith('/') ? root : `${root}/`;
  };

  const getCartItemsContainer = () => document.querySelector('#cart-drawer-items');

  const ensureSlot = () => {
    const cartItems = getCartItemsContainer();
    if (!cartItems) return null;

    let slot = document.getElementById(SLOT_ID);
    if (!slot) {
      slot = document.createElement('div');
      slot.id = SLOT_ID;
      slot.className = 'cart-drawer__recommendations-slot';
      slot.setAttribute('aria-live', 'polite');
      cartItems.after(slot);
    }

    return slot;
  };

  const getCartData = async () => {
    const response = await fetch(`${routeRoot()}cart.js`, {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' }
    });

    if (!response.ok) throw new Error(`Cart request failed with HTTP ${response.status}`);
    return response.json();
  };

  const getCartProductIds = (cart) => Array.from(new Set(
    (cart?.items || [])
      .map((item) => item.product_id)
      .filter(Boolean)
      .map(String)
  ));

  const getCartSignature = (cartProductIds) => [...cartProductIds].sort().join(',');

  const normalizeText = (value = '') => String(value)
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .trim();

  const inferTextDisciplines = (value) => {
    const text = normalizeText(value);
    const result = new Set();
    if (!text) return result;

    if (text.includes('hockey')) result.add('hockey');

    if (
      text.includes('boxeo')
      || text.includes('kickboxing')
      || text.includes('kick boxing')
      || text.includes('muay thai')
      || /\bmma\b/.test(text)
      || text.includes('artes marciales')
    ) result.add('boxeo');

    if (
      text.includes('futbol')
      || text.includes('futsal')
      || text.includes('futbolito')
      || text.includes('baby futbol')
      || text.includes('soccer')
      || text.includes('arquero')
      || text.includes('portero')
      || text.includes('goalkeeper')
    ) result.add('futbol');

    if (
      text.includes('voleibol')
      || text.includes('volleyball')
      || /\bvoley\b/.test(text)
    ) result.add('voleibol');

    if (
      text.includes('basquetbol')
      || text.includes('basketball')
      || text.includes('baloncesto')
    ) result.add('basquetbol');

    const isTableTennis =
      text.includes('ping pong')
      || text.includes('tenis de mesa')
      || text.includes('table tennis');

    if (isTableTennis) result.add('ping-pong');

    const isBeachPaddle =
      /\b(paleta|paletas|raqueta|raquetas)\s+de\s+playa\b/.test(text)
      || text.includes('set paletas de playa');

    if (isBeachPaddle) result.add('beach-paddle');
    if (text.includes('padel')) result.add('padel');
    if (text.includes('badminton')) result.add('badminton');
    if (!isTableTennis && /\btenis\b/.test(text)) result.add('tenis');

    if (
      text.includes('natacion')
      || text.includes('swimming')
      || text.includes('piscina')
      || text.includes('snorkel')
    ) result.add('natacion');

    if (
      text.includes('fitness')
      || text.includes('gimnasio')
      || /\bgym\b/.test(text)
      || text.includes('crossfit')
    ) result.add('fitness');

    return result;
  };

  const inferProductFamily = (value) => {
    const text = normalizeText(value);
    if (!text) return '';

    if (/\b(venda|vendas)\b/.test(text)) return 'wraps';
    if (/\b(guante|guantes)\b/.test(text)) return 'gloves';
    if (/\b(rodillera|rodilleras|codera|coderas|protector|protectores|espinillera|espinilleras|canillera|canilleras)\b/.test(text)) return 'protective';
    if (/\b(short|shorts|pantalon|pantalones|calza|calzas)\b/.test(text)) return 'apparel-bottom';
    if (/\b(polera|poleras|camiseta|camisetas|peto|petos)\b/.test(text)) return 'apparel-top';
    if (/\b(media|medias|calceta|calcetas)\b/.test(text)) return 'socks';
    if (/\b(gorro|gorra|gorros|gorras)\b/.test(text)) return 'cap';
    if (/\b(lente|lentes|antiparra|antiparras)\b/.test(text)) return 'goggles';
    if (/\b(naricera|nariceras|tapon|tapones)\b/.test(text)) return 'small-accessory';
    if (/\b(snorkel|mascara|mascaras)\b/.test(text)) return 'snorkel-mask';
    if (/\b(paleta|paletas)\b/.test(text)) return 'paddle';
    if (/\b(red|redes)\b/.test(text)) return 'net';
    if (/\b(funda|fundas|cubierta|cubiertas)\b/.test(text)) return 'cover';
    if (/\b(mesa|mesas)\b/.test(text)) return 'table';
    if (/\b(balon|balones|pelota|pelotas)\b/.test(text)) return 'ball';
    if (/\b(munequera|munequeras)\b/.test(text)) return 'wrist-support';
    if (/\b(cinturon|cinturones)\b/.test(text)) return 'belt';
    if (/\b(colchoneta|colchonetas|mat|mats)\b/.test(text)) return 'mat';
    if (/\b(cono|conos)\b/.test(text)) return 'cone';
    if (/\b(inflador|infladores|bomba|bombas)\b/.test(text)) return 'pump';
    if (/\b(bolso|bolsos|mochila|mochilas)\b/.test(text)) return 'bag';
    if (/\b(botella|botellas|caramayola|caramayolas)\b/.test(text)) return 'bottle';
    if (/\b(silbato|silbatos)\b/.test(text)) return 'whistle';

    return '';
  };

  const getCollectionHandles = (card) => (card.dataset.recommendationCollections || '')
    .split('|')
    .map((handle) => handle.trim())
    .filter(Boolean);

  const getSourceDisciplines = (item) => inferTextDisciplines(
    `${item?.product_title || item?.title || ''} ${item?.product_type || ''}`
  );

  const getCandidateDisciplines = (card) => {
    const textDisciplines = inferTextDisciplines(
      `${card.dataset.recommendationTitle || ''} ${card.dataset.recommendationProductType || ''}`
    );
    const result = new Set(textDisciplines);
    const hasSpecificRacketDiscipline = [...textDisciplines]
      .some((discipline) => RACKET_SUBDISCIPLINES.has(discipline));

    getCollectionHandles(card).forEach((handle) => {
      const discipline = TOP_LEVEL_COLLECTION_DISCIPLINES.get(handle);
      if (!discipline) return;
      if (discipline === 'racket' && hasSpecificRacketDiscipline) return;
      result.add(discipline);
    });

    return result;
  };

  const getSourceFamily = (item) => inferProductFamily(
    `${item?.product_title || item?.title || ''} ${item?.product_type || ''}`
  );

  const getCandidateFamily = (card) => {
    const familyFromText = inferProductFamily(
      `${card.dataset.recommendationTitle || ''} ${card.dataset.recommendationProductType || ''}`
    );
    if (familyFromText) return familyFromText;

    const specificCollection = getCollectionHandles(card)
      .find((handle) => !GENERIC_COLLECTION_HANDLES.has(handle));
    if (specificCollection) return `collection:${specificCollection}`;

    const productType = normalizeText(card.dataset.recommendationProductType || '');
    return productType ? `type:${productType}` : '';
  };

  const hasIntersection = (first, second) =>
    [...first].some((value) => second.has(value));

  const hasRacketSubdiscipline = (disciplines) =>
    [...disciplines].some((discipline) => RACKET_SUBDISCIPLINES.has(discipline));

  const areDisciplinesCompatible = (sourceDisciplines, candidateDisciplines) => {
    if (hasIntersection(sourceDisciplines, candidateDisciplines)) return true;

    if (
      sourceDisciplines.has('racket')
      && hasRacketSubdiscipline(candidateDisciplines)
    ) return true;

    if (
      candidateDisciplines.has('racket')
      && hasRacketSubdiscipline(sourceDisciplines)
    ) return true;

    return false;
  };

  const isCandidateCompatibleWithSource = (card, sourceItem) => {
    const sourceDisciplines = getSourceDisciplines(sourceItem);
    if (!sourceDisciplines.size) return true;

    const candidateDisciplines = getCandidateDisciplines(card);
    return !candidateDisciplines.size || areDisciplinesCompatible(sourceDisciplines, candidateDisciplines);
  };

  const diversifyRecommendationEntries = (entries, sourceFamily, usedFamilies) => {
    const remaining = [...entries];
    const ordered = [];

    while (remaining.length) {
      let pickIndex = 0;

      if (ordered.length || usedFamilies.size) {
        const complementaryIndex = remaining.findIndex((entry) =>
          entry.family
          && !usedFamilies.has(entry.family)
          && (!sourceFamily || entry.family !== sourceFamily)
        );

        const newFamilyIndex = remaining.findIndex((entry) =>
          entry.family && !usedFamilies.has(entry.family)
        );

        pickIndex = complementaryIndex >= 0
          ? complementaryIndex
          : newFamilyIndex >= 0
            ? newFamilyIndex
            : 0;
      }

      const [picked] = remaining.splice(pickIndex, 1);
      ordered.push(picked);
      if (picked.family) usedFamilies.add(picked.family);
    }

    return ordered;
  };

  const rankRecommendationCards = (cards, sourceItem) => {
    const sourceDisciplines = getSourceDisciplines(sourceItem);
    const sourceFamily = getSourceFamily(sourceItem);

    const entries = cards.map((card, index) => {
      const candidateDisciplines = getCandidateDisciplines(card);
      const bucket = sourceDisciplines.size && areDisciplinesCompatible(sourceDisciplines, candidateDisciplines)
        ? 0
        : candidateDisciplines.size
          ? 2
          : 1;

      return {
        card,
        index,
        bucket,
        family: getCandidateFamily(card)
      };
    });

    if (!sourceDisciplines.size) {
      entries.forEach((entry) => { entry.bucket = 0; });
    }

    const usedFamilies = new Set();
    return [0, 1, 2]
      .flatMap((bucket) => diversifyRecommendationEntries(
        entries.filter((entry) => entry.bucket === bucket),
        sourceFamily,
        usedFamilies
      ))
      .map(({ card }) => card);
  };

  const getPrimaryDiscipline = (item) => {
    const disciplines = getSourceDisciplines(item);
    return disciplines.values().next().value || '';
  };

  const getRecommendationSources = (cart) => {
    const items = cart?.items || [];
    if (!items.length) return [];

    const seenDisciplines = new Set();
    const sources = [];

    items.forEach((item) => {
      if (!item?.product_id || sources.length >= MAX_VISIBLE_RECOMMENDATIONS) return;

      const discipline = getPrimaryDiscipline(item);
      if (!discipline || seenDisciplines.has(discipline)) return;

      seenDisciplines.add(discipline);
      sources.push(item);
    });

    if (sources.length) return sources;

    return items[0]?.product_id ? [items[0]] : [];
  };

  const getSourceSignature = (sourceItems) => sourceItems
    .map((item) => String(item?.product_id || ''))
    .filter(Boolean)
    .join(',');

  const clearRecommendations = () => {
    requestController?.abort();
    requestController = null;
    const slot = document.getElementById(SLOT_ID);
    if (!slot) return;

    slot.innerHTML = '';
    slot.removeAttribute('data-product-id');
    slot.removeAttribute('data-source-signature');
    slot.removeAttribute('data-cart-signature');
    slot.removeAttribute('aria-busy');
  };

  const getRecommendationCards = (source, cartProducts, sourceItem) => {
    if (!source) return [];

    const cards = Array.from(source.querySelectorAll('[data-recommendation-product-id]'))
      .filter((card) => {
        const recommendationProductId = card.dataset.recommendationProductId;
        const isNotInCart = !recommendationProductId || !cartProducts.has(recommendationProductId);
        return isNotInCart && isCandidateCompatibleWithSource(card, sourceItem);
      });

    return rankRecommendationCards(cards, sourceItem);
  };

  const getNextUniqueCard = (queue, seenProductIds) => {
    while (queue.length) {
      const card = queue.shift();
      const productId = card.dataset.recommendationProductId;

      if (productId && seenProductIds.has(productId)) continue;
      if (productId) seenProductIds.add(productId);
      return card;
    }

    return null;
  };

  const selectBalancedRecommendations = (queues) => {
    const selected = [];
    const seenProductIds = new Set();

    queues.forEach((queue) => {
      if (selected.length >= MAX_VISIBLE_RECOMMENDATIONS) return;
      const card = getNextUniqueCard(queue, seenProductIds);
      if (card) selected.push(card);
    });

    while (selected.length < MAX_VISIBLE_RECOMMENDATIONS) {
      let added = false;

      for (const queue of queues) {
        if (selected.length >= MAX_VISIBLE_RECOMMENDATIONS) break;

        const card = getNextUniqueCard(queue, seenProductIds);
        if (!card) continue;

        selected.push(card);
        added = true;
      }

      if (!added) break;
    }

    return selected;
  };

  const buildBalancedRecommendationMarkup = (recommendationResults, cartProductIds) => {
    const validResults = recommendationResults.filter(({ source }) => source);
    if (!validResults.length) return '';

    const cartProducts = new Set(cartProductIds);
    const queues = validResults.map(({ source, sourceItem }) =>
      getRecommendationCards(source, cartProducts, sourceItem)
    );
    const selectedCards = selectBalancedRecommendations(queues);

    if (!selectedCards.length) return '';

    const baseSource = validResults[0].source;
    const list = baseSource.querySelector('.cart-drawer__recommendations-products');
    if (!list) return '';

    list.innerHTML = '';
    selectedCards.forEach((card) => list.append(card.cloneNode(true)));

    return baseSource.innerHTML.trim();
  };

  const fetchRecommendationSource = async (sourceItem, controller) => {
    const productId = sourceItem?.product_id;
    if (!productId) return null;

    const url = `${routeRoot()}recommendations/products?section_id=${encodeURIComponent(SECTION_ID)}&product_id=${encodeURIComponent(productId)}&limit=${REQUESTED_RECOMMENDATIONS}&intent=related`;
    const response = await fetch(url, {
      credentials: 'same-origin',
      signal: controller.signal
    });

    if (!response.ok) throw new Error(`Recommendations request failed with HTTP ${response.status}`);

    const text = await response.text();
    const parsed = new DOMParser().parseFromString(text, 'text/html');

    return {
      sourceItem,
      source: parsed.querySelector('.cart-drawer__recommendations-response')
    };
  };

  const loadRecommendations = async (sourceItems, cartSignature, cartProductIds) => {
    const slot = ensureSlot();
    if (!slot || !sourceItems.length) return;

    const sourceSignature = getSourceSignature(sourceItems);
    const sameRequest =
      slot.dataset.sourceSignature === sourceSignature
      && slot.dataset.cartSignature === cartSignature;

    if (sameRequest) {
      if (slot.getAttribute('aria-busy') === 'true') return;
      if (slot.innerHTML.trim()) return;
    }

    if (requestController) requestController.abort();
    const controller = new AbortController();
    requestController = controller;

    slot.dataset.sourceSignature = sourceSignature;
    slot.dataset.cartSignature = cartSignature;
    slot.removeAttribute('data-product-id');
    slot.setAttribute('aria-busy', 'true');

    try {
      const recommendationResults = await Promise.all(
        sourceItems.map(async (sourceItem) => {
          try {
            return await fetchRecommendationSource(sourceItem, controller);
          } catch (error) {
            if (error.name === 'AbortError') throw error;
            console.warn('DeporteMania cart recommendations could not load a recommendation source.', error);
            return null;
          }
        })
      );

      if (requestController !== controller) return;

      slot.innerHTML = buildBalancedRecommendationMarkup(
        recommendationResults.filter(Boolean),
        cartProductIds
      );
    } catch (error) {
      if (error.name !== 'AbortError' && requestController === controller) {
        console.warn('DeporteMania cart recommendations could not be loaded.', error);
        slot.innerHTML = '';
      }
    } finally {
      if (requestController === controller) {
        slot.removeAttribute('aria-busy');
        requestController = null;
      }
    }
  };

  const refreshRecommendations = async () => {
    const currentRefresh = ++refreshId;
    requestController?.abort();
    requestController = null;
    document.getElementById(SLOT_ID)?.removeAttribute('aria-busy');
    try {
      const cart = await getCartData();
      if (currentRefresh !== refreshId) return;
      const sourceItems = getRecommendationSources(cart);

      if (!sourceItems.length) {
        clearRecommendations();
        return;
      }

      const cartProductIds = getCartProductIds(cart);
      await loadRecommendations(
        sourceItems,
        getCartSignature(cartProductIds),
        cartProductIds
      );
    } catch (error) {
      if (currentRefresh !== refreshId) return;
      console.warn('DeporteMania cart recommendations could not read the cart.', error);
      clearRecommendations();
    }
  };

  const scheduleRefresh = () => {
    window.clearTimeout(scheduledRefresh);
    scheduledRefresh = window.setTimeout(refreshRecommendations, 120);
  };

  const mutationTouchesCartItems = (mutation) => {
    const target = mutation.target;
    if (target instanceof Element) {
      if (target.id === 'cart-drawer-items' || target.closest('#cart-drawer-items')) return true;
    }

    return Array.from(mutation.addedNodes).some((node) => {
      if (!(node instanceof Element)) return false;
      return node.id === 'cart-drawer-items' || Boolean(node.querySelector('#cart-drawer-items'));
    });
  };

  const startObserver = () => {
    observer = new MutationObserver((mutations) => {
      if (mutations.some(mutationTouchesCartItems)) scheduleRefresh();
    });

    observer.observe(document.body, { childList: true, subtree: true });
  };

  const showCartError = (errors = []) => {
    const messages = Array.isArray(errors) ? errors.filter(Boolean) : [errors].filter(Boolean);
    if (!messages.length) return;
    document.querySelector('push-messages')?.push(messages);
  };

  const handleRecommendationAdd = (event) => {
    const button = event.target.closest('[data-cart-recommendation-add]');
    if (!button) return;

    event.preventDefault();

    if (button.disabled || button.classList.contains('loading')) return;

    const variantId = button.dataset.variantId;
    const productUrl = button.dataset.productUrl;

    if (!variantId || !window.cartBase?.addToCart) {
      if (productUrl) window.location.href = productUrl;
      return;
    }

    const body = new FormData();
    body.append('id', variantId);
    body.append('quantity', '1');

    const target = {
      onError(errors) {
        showCartError(errors);
      }
    };

    window.cartBase.addToCart({
      body,
      source: 'product-form',
      target,
      enableEventCall: true,
      onLoadingToggle: (isLoading) => {
        button.disabled = isLoading;
        button.toggleAttribute('aria-disabled', isLoading);
        button.classList.toggle('loading', isLoading);
        window.cartBase?.toggleCartLoading?.({ isLoading });

        if (!isLoading) scheduleRefresh();
      }
    });
  };

  const init = () => {
    startObserver();
    document.addEventListener('click', handleRecommendationAdd);
    refreshRecommendations();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
